import { Popover, Tooltip, Typography } from '@mui/material'
import { useTriggerAutomaticSettingsRunMutation } from 'api/automaticSettingRuns/automaticSettingRuns.api'
import { type MouseEvent, type ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'ui/atoms'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import { DatePickerPeriod } from 'ui/molecules/pickers/DatePicker/DatePickerPeriod'
import {
  isRunWindowEmpty,
  isRunWindowTooLong,
  MAX_RUN_WINDOW_DAYS,
  previousWeek,
  runWindowOfDays,
} from './runWindow.helper'

type AutomaticSettingsRunControlProps = {
  systemId: number
}

export function AutomaticSettingsRunControl({ systemId }: AutomaticSettingsRunControlProps): ReactNode {
  const { t } = useTranslation()
  const { mutate: triggerRun, isPending } = useTriggerAutomaticSettingsRunMutation()
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLElement | null>(null)
  const [days, setDays] = useState(() => previousWeek(new Date()))

  const runWindow = runWindowOfDays(days.firstDay, days.lastDay)
  const isTooLong = isRunWindowTooLong(runWindow)

  const handlePopoverOpen = (event: MouseEvent<HTMLElement>) => {
    setPopoverAnchorEl(event.currentTarget)
  }

  const handlePopoverClose = () => {
    setPopoverAnchorEl(null)
  }

  const handleRun = () => {
    triggerRun({ system: systemId, ...runWindow })
    handlePopoverClose()
  }

  return (
    <>
      <Button
        icon="fal fa-play"
        primary
        className="mr-2"
        onClick={handlePopoverOpen}
        tooltip={t('Pick time period to run the automatic settings for')}
      >
        {t('Run automatic settings')}
      </Button>

      <Popover
        open={Boolean(popoverAnchorEl)}
        anchorEl={popoverAnchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <div className="p-5">
          <Typography variant="h6" sx={{ fontWeight: 'bold' }} display="inline" alignSelf="center">
            {t('Run automatic settings')}
          </Typography>
          <InfoBanner
            text={t('Replace the automatic settings in the selected days by running every enabled rule')}
            style="info"
            variant="text"
          />
          <DatePickerPeriod
            granularity="day"
            startDate={days.firstDay}
            endDate={days.lastDay}
            onSelect={(firstDay, lastDay) => {
              if (firstDay && lastDay) {
                setDays({ firstDay, lastDay })
              }
            }}
          />
          <Button secondary className="ml-2" onClick={handlePopoverClose}>
            {t('Cancel')}
          </Button>
          <Tooltip
            title={
              isTooLong
                ? t('The selected period cannot be longer than {{maxDays}} days', { maxDays: MAX_RUN_WINDOW_DAYS })
                : ''
            }
            arrow
            placement="top"
          >
            <div className="inline-block">
              <Button
                primary
                className="ml-2"
                disabled={isRunWindowEmpty(runWindow) || isTooLong || isPending}
                onClick={handleRun}
              >
                {t('Run')}
              </Button>
            </div>
          </Tooltip>
        </div>
      </Popover>
    </>
  )
}
